import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { LoadingState } from '../../../../components/AsyncStates';
import { cn } from '@/utils/cn';
import { useMyWorkPoints } from '@/hooks/useMyWorkPoints';
import { useCurrency } from '@/hooks/useCurrency';
import { MatchRole, resolveEnum } from '@/models/tournament';
import { formatLocalDateParam, parseApiWallClock } from '@/utils/apiDate';
import { appDayMonth, appWeekday } from '@/utils/appDateFormat';
import { DateField } from '@/components/dateFields';

const roleKeys: Record<MatchRole, string> = {
  [MatchRole.HeadReferee]: 'Head Referee',
  [MatchRole.AssistantReferee]: 'Assistant Referee',
  [MatchRole.FourthOfficial]: 'Fourth Official',
  [MatchRole.Commissioner]: 'Commissioner',
};

const WorkPointsTab: React.FC = () => {
  const { t } = useTranslation('tournament');

  // Default to current month. NEVER toISOString() — east of UTC it drops the last day of the month.
  const now = new Date();
  const defaultStart = formatLocalDateParam(new Date(now.getFullYear(), now.getMonth(), 1));
  const defaultEnd = formatLocalDateParam(new Date(now.getFullYear(), now.getMonth() + 1, 0));

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const { data: summary, loading, error, refetch } = useMyWorkPoints({ startDate, endDate });
  const { format: formatCurrency } = useCurrency();

  // `loading` flips on EVERY date change and the old code early-returned a spinner on it, which
  // unmounted the two date inputs the user was typing into: the date being edited vanished
  // mid-keystroke and focus was lost. Only the very first fetch (nothing to show yet) gets the
  // spinner; later fetches keep everything mounted and just dim the results.
  const isInitialLoad = loading && !summary;
  const isRefetching = loading && !!summary;

  const formatDate = (dateString: string): string => {
    const date = parseApiWallClock(dateString) ?? new Date(dateString);
    return `${appWeekday(date, 'short')} ${appDayMonth(date)}`;
  };

  return (
    <div className="space-y-6" data-testid="work-points-tab">
      {error && (
        <div
          role="alert"
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3"
          data-testid="work-points-error"
        >
          <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-red-900 dark:text-red-200">{t('Error loading work points')}</p>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={refetch} className="ms-auto" data-testid="work-points-retry-btn">
            {t('Try Again')}
          </Button>
        </div>
      )}

      {/* Date range + the totals FOR THAT RANGE.
          The three big-number cards that used to sit above this were a verbatim repeat of the three
          on the Assignments tab (now the page-header stats) — the same three numbers rendered twice,
          two clicks apart. They survive here as one inline line because, unlike the header, these
          follow the range the user picks right next to them. */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-4">
          <DateField
            id="wp-start-date"
            label={t('Start Date')}
            value={startDate}
            onChange={(v) => setStartDate(v)}
            className="w-44"
            data-testid="wp-start-date"
          />
          <DateField
            id="wp-end-date"
            label={t('End Date')}
            value={endDate}
            onChange={(v) => setEndDate(v)}
            className="w-44"
            data-testid="wp-end-date"
          />

          <div
            className={cn(
              'flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm ms-auto pb-2',
              'transition-opacity duration-200 ease-out motion-reduce:transition-none',
              isRefetching && 'opacity-60',
            )}
            aria-busy={isRefetching}
          >
            <span className="text-muted-foreground" data-testid="wp-card-total-points">
              {t('Total Work Points')}
              <span className="ms-1.5 text-base font-semibold text-foreground">{summary?.totalWorkPoints ?? 0}</span>
            </span>
            <span className="text-muted-foreground" data-testid="wp-card-currency">
              {t('Total Currency Amount')}
              <span className="ms-1.5 text-base font-semibold text-foreground">{formatCurrency(summary?.totalCurrencyAmount ?? 0)}</span>
            </span>
            <span className="text-muted-foreground" data-testid="wp-card-match-count">
              {t('Match Count')}
              <span className="ms-1.5 text-base font-semibold text-foreground">{summary?.matchCount ?? 0}</span>
            </span>
          </div>
        </div>
      </div>

      {isInitialLoad && (
        <div className="py-8">
          <LoadingState title={t('Loading data...')} />
        </div>
      )}

      {/* Recent Matches List */}
      {!isInitialLoad && (
        <div
          className={cn(
            'transition-opacity duration-200 ease-out motion-reduce:transition-none',
            isRefetching && 'opacity-60 pointer-events-none',
          )}
          aria-busy={isRefetching}
        >
          <h3 className="text-sm font-semibold text-foreground mb-3">{t('Recent Matches')}</h3>
          {summary?.recentMatches && summary.recentMatches.length > 0 ? (
            <div className="space-y-2">
              {summary.recentMatches.map((match) => (
                <div
                  key={match.matchId}
                  className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
                  data-testid={`wp-match-${match.matchId}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">{match.tournamentName}</p>
                      <p className="text-sm font-semibold text-foreground">
                        {match.team1Name} <span className="text-muted-foreground">{t('vs')}</span> {match.team2Name}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center">
                          <Icon name="Calendar" size={12} className="me-1 text-muted-foreground/70" />
                          {formatDate(match.scheduledDateTime)}
                        </span>
                        <span className="flex items-center">
                          <Icon name="MapPin" size={12} className="me-1 text-muted-foreground/70" />
                          {match.location}
                        </span>
                        <span className="flex items-center">
                          <Icon name="UserCog" size={12} className="me-1 text-primary" />
                          <span className="font-medium text-primary">{t(roleKeys[(resolveEnum(MatchRole, match.role) as MatchRole)] ?? 'Unknown')}</span>
                        </span>
                      </div>
                    </div>
                    {match.workPoints > 0 && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                        <Icon name="Zap" size={12} className="me-1" />
                        {match.workPoints} {t('pts')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !error && (
              <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="empty-work-points">
                <Icon name="Calculator" size={48} className="mx-auto text-muted-foreground/40 mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">{t('No work points data')}</h3>
                <p className="text-muted-foreground">{t('No work points found for the selected date range.')}</p>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default WorkPointsTab;
